/**
 * 서버와 클라이언트가 공유하는 순수 게임 로직.
 * 랜덤/권한 결정은 서버에서만 하지만, 후보 계산과 턴 배정 규칙은 공통입니다.
 */
import { DINING_OPTIONS, MAIN_CATEGORIES, findMainCategory, findSubCategory } from '../data/menu';
import { SELECTION_STAGES, type GameStage, type Participant, type SelectOption, type Selection, type SelectionStage } from '../types/room';

export function isSelectionStage(stage: GameStage): stage is SelectionStage {
  return (SELECTION_STAGES as readonly string[]).includes(stage);
}

export function stageIndex(stage: SelectionStage): number {
  return SELECTION_STAGES.indexOf(stage);
}

export function nextStage(stage: SelectionStage): GameStage {
  const idx = stageIndex(stage);
  return SELECTION_STAGES[idx + 1] ?? 'RESULT';
}

/** 현재 단계에서 선택 가능한 후보. 완료된 선택 기록을 바탕으로 계산합니다. */
export function getOptionsForStage(stage: GameStage, selections: Selection[]): SelectOption[] {
  switch (stage) {
    case 'DINING_TYPE':
      return DINING_OPTIONS;
    case 'MAIN_CATEGORY':
      return MAIN_CATEGORIES.map(({ id, name, emoji, description }) => ({ id, name, emoji, description }));
    case 'SUB_CATEGORY': {
      const main = selections.find((s) => s.stage === 'MAIN_CATEGORY');
      if (!main) return [];
      return (findMainCategory(main.optionId)?.subCategories ?? []).map(({ id, name, emoji }) => ({ id, name, emoji }));
    }
    case 'MENU': {
      const main = selections.find((s) => s.stage === 'MAIN_CATEGORY');
      const sub = selections.find((s) => s.stage === 'SUB_CATEGORY');
      if (!main || !sub) return [];
      return (findSubCategory(main.optionId, sub.optionId)?.menus ?? []).map(({ id, name, emoji }) => ({ id, name, emoji }));
    }
    case 'RESULT':
      return [];
  }
}

export function findOption(stage: GameStage, selections: Selection[], optionId: string): SelectOption | undefined {
  return getOptionsForStage(stage, selections).find((o) => o.id === optionId);
}

/**
 * 이 선택으로 게임이 끝나는지 판단합니다.
 * - MENU 단계는 항상 마지막
 * - SUB_CATEGORY 단계는 선택한 중분류가 가게를 결정하는 분류(decidesVenue)면 여기서 종료
 */
export function isFinalSelection(stage: SelectionStage, selections: Selection[], optionId: string): boolean {
  if (stage === 'MENU') return true;
  if (stage !== 'SUB_CATEGORY') return false;
  const main = selections.find((s) => s.stage === 'MAIN_CATEGORY');
  if (!main) return false;
  return findSubCategory(main.optionId, optionId)?.decidesVenue === true;
}

/** 3단계 종료 시 참고용으로 보여줄 메뉴 목록 */
export function suggestedMenusFor(selections: Selection[], subId: string): SelectOption[] {
  const main = selections.find((s) => s.stage === 'MAIN_CATEGORY');
  if (!main) return [];
  return (findSubCategory(main.optionId, subId)?.menus ?? []).map(({ id, name, emoji }) => ({ id, name, emoji }));
}

/**
 * 턴 배정 규칙: 게임 시작 시 생성한 무작위 순서를 단계마다 순환 적용합니다.
 * 담당 참여자가 퇴장(active=false)했으면 순서상 다음 활성 참여자에게 넘깁니다.
 * 활성 참여자가 없으면 null을 반환합니다.
 */
export function assignChooser(
  turnOrder: readonly string[],
  stageIdx: number,
  participants: readonly Pick<Participant, 'id' | 'active'>[],
): string | null {
  const n = turnOrder.length;
  if (n === 0) return null;
  const activeIds = new Set(participants.filter((p) => p.active).map((p) => p.id));
  const start = stageIdx % n;
  for (let k = 0; k < n; k += 1) {
    const id = turnOrder[(start + k) % n];
    if (id !== undefined && activeIds.has(id)) return id;
  }
  return null;
}

/** 결과 경로를 짧은 라벨로 변환 (배달해서 먹기 → 배달) */
export function shortLabel(selection: Pick<Selection, 'stage' | 'optionId' | 'optionName'>): string {
  if (selection.stage === 'DINING_TYPE') {
    return selection.optionId === 'delivery' ? '배달' : '외식';
  }
  return selection.optionName;
}

/** 공유 텍스트 생성 (결과 공유용) */
export function buildShareText(serviceName: string, selections: Selection[], menuName: string, decidedAtStage: 'SUB_CATEGORY' | 'MENU' = 'MENU'): string {
  const path = SELECTION_STAGES.map((stage) => selections.find((s) => s.stage === stage))
    .filter((s): s is Selection => Boolean(s))
    .map(shortLabel)
    .join(' → ');
  const headline = decidedAtStage === 'SUB_CATEGORY' ? `오늘 점심은 ${menuName}! (메뉴는 각자 골라요)` : `오늘 점심은 ${menuName}!`;
  return `🍴 ${serviceName}\n\n${headline}\n\n${path}\n\n오늘의 점심 고민 해결!`;
}
